import {
  Box,
  Button,
  CircularProgress,
  Divider,
  useTheme,
} from "@mui/material";
import { AddPoc } from "./AddPoc";
import { VendorPOC } from "../../../models/Vendors/VendorPOC";
import React, { useState } from "react";
import { PocListing } from "./PocListing";
import { useLocation, useNavigate } from "react-router-dom";
import { useStores } from "../../../use-stores";
import { useNotification } from "../../../utils/NotificationsContext";
import CustomTypography from "../../components/common/form/CustomTypography";

interface PocInterface {
  pocs: VendorPOC[];
  modal: boolean;
  onClose?: () => void;
}

export const Poc: React.FC<PocInterface> = ({ pocs, modal, onClose }) => {
  const { vendorStore } = useStores();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const vendorId = queryParams.get("vendor-id");
  const { showError, showSuccess } = useNotification();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const applyVendor = () => {
    setLoading(true);
    if (
      vendorStore.vendorDetails?.poc_list &&
      vendorStore.vendorDetails.poc_list.length > 0
    ) {
      const hasPrimary = vendorStore.vendorDetails.poc_list.some((poc) => {
        return poc.is_primary === "1" || String(poc.is_primary) === "1";
      });
      if (!hasPrimary && vendorStore.vendorDetails.poc_list.length > 0) {
        vendorStore.vendorDetails.poc_list[0].is_primary = "1";
      }
      vendorStore.vendorDetails.poc_list =
        vendorStore.vendorDetails.poc_list.map((poc) => {
          const isPrimary =
            poc.is_primary === "1" || String(poc.is_primary) === "1";
          const { is_primary, ...restPoc } = poc;
          return {
            ...restPoc,
            is_primary: isPrimary ? "1" : "0",
          };
        });
    }
    if (vendorId) {
      updateVendor();
    } else {
      addVendor();
    }
  };
  const isValidGSTIN = (value: string): boolean => {
    const trimmed = (value || "").trim().toUpperCase();
    if (trimmed.length !== 15) return false;
    return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/i.test(trimmed);
  };
  const updateVendor = () => {
    if (!vendorStore.vendorDetails?.name?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter vendor name");
      return;
    }
    if (!vendorStore.vendorDetails?.email?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter email");
      return;
    }
    if (!vendorStore.vendorDetails?.gst_number?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter GST number");
      return;
    }
    if (!isValidGSTIN(vendorStore.vendorDetails.gst_number)) {
      setLoading(false);
      showError(
        "Error occurred",
        "Invalid GST number. It must be 15 characters (e.g. 22AABCU9603R1ZX)",
      );
      return;
    }
    if (!vendorStore.vendorDetails?.pan_number?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter PAN number");
      return;
    }
    if (!vendorStore.vendorDetails?.payment_term) {
      setLoading(false);
      showError("Error occurred", "Please select payment term");
      return;
    }
    if (!vendorStore.vendorDetails?.hasMSME) {
      setLoading(false);
      showError("Error occurred", "Please select MSME status");
      return;
    } else if (!vendorStore.vendorDetails?.hasCatalog) {
      setLoading(false);
      showError("Error occurred", "Please select Catalog status");
      return;
    } else if (
      vendorStore.vendorDetails?.hasMSME === "yes" &&
      !vendorStore.vendorDetails?.msme_link
    ) {
      setLoading(false);
      showError("Error occurred", "Please upload MSME certificate");
      return;
    } else if (
      vendorStore.vendorDetails?.hasCatalog === "yes" &&
      !vendorStore.vendorDetails?.products_catalog_link
    ) {
      setLoading(false);
      showError(
        "Error occurred",
        "Please provide product catalog (upload or link)",
      );
      return;
    } else if (
      vendorStore.vendorDetails?.types?.some((type) => type.type_id === 3) &&
      !vendorStore.vendorDetails?.supplier_type
    ) {
      setLoading(false);
      showError(
        "Error occurred",
        "Please select supplier type (Brand or Distributor)",
      );
      return;
    }
    if (!vendorStore.vendorDetails?.address?.address_line_1?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter Address Line 1");
      return;
    }
    if (!vendorStore.vendorDetails?.address?.address_line_2?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter Address Line 2");
      return;
    }
    if (!vendorStore.vendorDetails?.address?.city?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter City");
      return;
    }
    if (!vendorStore.vendorDetails?.address?.state?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter State");
      return;
    }
    if (!vendorStore.vendorDetails?.address?.postal_code?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter Postal Code");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.account_holder_name?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter bank account holder name");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.bank_name?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter bank name");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.branch_name?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter branch name");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.account_number?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter account number");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.ifsc_code?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter IFSC code");
      return;
    }
    if (
      !vendorStore.vendorDetails?.poc_list ||
      vendorStore.vendorDetails?.poc_list?.length === 0
    ) {
      setLoading(false);
      showError("Error occurred", "Please add at least 1 POC");
      return;
    }
    if (
      !vendorStore.vendorDetails?.types ||
      vendorStore.vendorDetails?.types?.length === 0
    ) {
      setLoading(false);
      showError("Error occurred", "Please select vendor type");
      return;
    }
    if (!vendorStore.vendorDetails?.pan_card_link) {
      setLoading(false);
      showError("Error occurred", "Please upload PAN card document");
      return;
    }
    if (!vendorStore.vendorDetails?.gstin_certificate_link) {
      setLoading(false);
      showError("Error occurred", "Please upload GST certificate document");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details_link) {
      setLoading(false);
      showError("Error occurred", "Please upload bank details document");
      return;
    }
    vendorStore
      .updateVendorById(vendorStore.vendorDetails!)
      .then(() => {
        setLoading(false);
        showSuccess("Vendor updated successfully");
      })
      .catch((err) => {
        setLoading(false);
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => setLoading(false));
  };

  const addVendor = () => {
    if (!vendorStore.vendorDetails?.name?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter vendor name");
      return;
    }
    if (!vendorStore.vendorDetails?.email?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter email");
      return;
    }
    if (!vendorStore.vendorDetails?.gst_number?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter GST number");
      return;
    }
    if (!isValidGSTIN(vendorStore.vendorDetails.gst_number)) {
      setLoading(false);
      showError(
        "Error occurred",
        "Invalid GST number. It must be 15 characters (e.g. 22AABCU9603R1ZX)",
      );
      return;
    }
    if (!vendorStore.vendorDetails?.pan_number?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter PAN number");
      return;
    }
    if (!vendorStore.vendorDetails?.payment_term) {
      setLoading(false);
      showError("Error occurred", "Please select payment term");
      return;
    }
    if (!vendorStore.vendorDetails?.hasMSME) {
      setLoading(false);
      showError("Error occurred", "Please select MSME status");
      return;
    } else if (!vendorStore.vendorDetails?.hasCatalog) {
      setLoading(false);
      showError("Error occurred", "Please select Catalog status");
      return;
    } else if (
      vendorStore.vendorDetails?.hasMSME === "yes" &&
      !vendorStore.vendorDetails?.msme_link
    ) {
      setLoading(false);
      showError("Error occurred", "Please upload MSME certificate");
      return;
    } else if (
      vendorStore.vendorDetails?.hasCatalog === "yes" &&
      !vendorStore.vendorDetails?.products_catalog_link
    ) {
      setLoading(false);
      showError(
        "Error occurred",
        "Please provide product catalog (upload or link)",
      );
      return;
    } else if (
      vendorStore.vendorDetails?.types?.some((type) => type.type_id === 3) &&
      !vendorStore.vendorDetails?.supplier_type
    ) {
      setLoading(false);
      showError(
        "Error occurred",
        "Please select supplier type (Brand or Distributor)",
      );
      return;
    }
    if (!vendorStore.vendorDetails?.address?.address_line_1?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter Address Line 1");
      return;
    }
    if (!vendorStore.vendorDetails?.address?.address_line_2?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter Address Line 2");
      return;
    }
    if (!vendorStore.vendorDetails?.address?.city?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter City");
      return;
    }
    if (!vendorStore.vendorDetails?.address?.state?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter State");
      return;
    }
    if (!vendorStore.vendorDetails?.address?.postal_code?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter Postal Code");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.account_holder_name?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter bank account holder name");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.bank_name?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter bank name");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.branch_name?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter branch name");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.account_number?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter account number");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details?.ifsc_code?.trim()) {
      setLoading(false);
      showError("Error occurred", "Please enter IFSC code");
      return;
    }
    if (
      !vendorStore.vendorDetails?.poc_list ||
      vendorStore.vendorDetails?.poc_list?.length === 0
    ) {
      setLoading(false);
      showError("Error occurred", "Please add at least 1 POC");
      return;
    }
    if (
      !vendorStore.vendorDetails?.types ||
      vendorStore.vendorDetails?.types?.length === 0
    ) {
      setLoading(false);
      showError("Error occurred", "Please select vendor type");
      return;
    }
    if (!vendorStore.vendorDetails?.pan_card_link) {
      setLoading(false);
      showError("Error occurred", "Please upload PAN card document");
      return;
    }
    if (!vendorStore.vendorDetails?.gstin_certificate_link) {
      setLoading(false);
      showError("Error occurred", "Please upload GST certificate document");
      return;
    }
    if (!vendorStore.vendorDetails?.bank_details_link) {
      setLoading(false);
      showError("Error occurred", "Please upload bank details document");
      return;
    }

    vendorStore
      .addVendor()
      .then(() => {
        setLoading(false);
        showSuccess("Vendor added successfully");
        if (!modal) {
          navigate("/vendors/listing");
        } else {
          onClose && onClose();
        }
      })
      .catch((err) => {
        setLoading(false);
        showError(
          "Error occurred",
          err?.response?.data?.message ||
            "Something went wrong! Please try again later.",
        );
      })
      .finally(() => setLoading(false));
  };

  const theme = useTheme();

  return (
    <Box>
      <Box sx={{ mb: 0 }}>
        <CustomTypography
          variant="heading5"
          fontWeight={700}
          sx={{ color: theme.palette.primary.main, mb: 0.5 }}
        >
          Point of Contact (POC)
        </CustomTypography>
        <Divider sx={{ mt: 1, mb: 1 }} />
      </Box>
      <PocListing pocs={pocs} />
      <Box sx={{ mt: 3 }}>
        <AddPoc />
      </Box>
      <Box sx={{ mt: 4 }}>
        {loading ? (
          <Button
            sx={{
              width: "100%",
              py: 1.5,
              borderRadius: "8px",
              textTransform: "none",
              fontSize: "1rem",
              fontWeight: 600,
            }}
            variant="contained"
            color="primary"
            onClick={() => applyVendor()}
            disabled
          >
            <CircularProgress size={24} color="inherit" />
          </Button>
        ) : (
          <Button
            sx={{
              width: "100%",
              px: 1.5,
              py: 1,
              right: 0,
            }}
            variant="contained"
            color="primary"
            onClick={() => applyVendor()}
          >
            {vendorId ? "Update Vendor" : "Add Vendor"}
          </Button>
        )}
      </Box>
    </Box>
  );
};
